// bookings/components/tabs/accessories/AccessoriesSelectedPanel.jsx
import React from "react";
import { Form, Button, InputGroup } from "react-bootstrap";

const AccessoriesSelectedPanel = ({
    customerCurrencyCode,
    leftAccessories = [],
    addPersonalized = () => { },
    removePersonalized = () => { },
    updateLeftField = () => { },
    sizeOptions = {},
    toCents,
    fromCents,
}) => {
    return (
        <div className="acc-panel acc-selected">
            {/* Header */}
            <div className="acc-panel__header">
                <h5 className="acc-panel__title">
                    <i className="bi bi-list-check"></i>
                    <span>Current Accessories</span>
                </h5>

                <span className="acc-panel__count">{leftAccessories.length}</span>

                <div className="acc-panel__actions">
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={addPersonalized}
                        className="acc-btn"
                    >
                        <i className="bi bi-plus-circle me-1"></i>
                        Add Personalized
                    </Button>
                </div>
            </div>

            {/* Body */}
            {leftAccessories.length === 0 ? (
                <div className="acc-panel__body">
                    <div className="text-center py-5 text-muted">
                        <i className="bi bi-inbox fs-1"></i>
                        <p className="mt-3 mb-0">No accessories selected</p>
                    </div>
                </div>
            ) : (
                <div className="acc-panel__scroll acc-panel__body">
                    {leftAccessories.map((accessory, index) => {
                        const isPersonalized = accessory.status === "personalized";
                        const isUnavailable =
                            accessory.status !== "personalized" &&
                            accessory.isAvailable === false;

                        const key =
                            accessory.tempId ||
                            accessory.shopMotorcycleExtraId ||
                            `acc-${index}`;

                        const sizeList = accessory.shopMotorcycleExtraId
                            ? sizeOptions[accessory.shopMotorcycleExtraId]
                            : null;

                        const statusBadge = isPersonalized ? (
                            <span className="acc-state acc-state--personal">Personalized</span>
                        ) : isUnavailable ? (
                            <span className="acc-state acc-state--warn">Unavailable</span>
                        ) : null;

                        const displayPrice =
                            accessory.priceText ??
                            (typeof fromCents === "function"
                                ? String(fromCents(accessory.priceCents ?? 0))
                                : String(accessory.priceCents ?? 0));

                        return (
                            <div
                                key={key}
                                className={`acc-selected__item ${isUnavailable ? "opacity-75" : ""
                                    }`}
                            >
                                {/* Top row: name + status + price */}
                                <div className="acc-selected__header">
                                    <div className="acc-selected__name">
                                        {isPersonalized ? (
                                            <>
                                                <Form.Control
                                                    size="sm"
                                                    type="text"
                                                    placeholder="Accessory name..."
                                                    value={accessory.extraName || ""}
                                                    onChange={(e) =>
                                                        updateLeftField(
                                                            accessory.shopMotorcycleExtraId,
                                                            accessory.tempId,
                                                            "extraName",
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                                {statusBadge}
                                            </>
                                        ) : (
                                            <>
                                                <div className="acc-selected__nameText">
                                                    {accessory.extraName || "Accessory"}
                                                </div>
                                                {statusBadge}
                                            </>
                                        )}
                                    </div>

                                    <div className="acc-selected__meta" style={{ minWidth: 220 }}>
                                        <InputGroup size="sm" className="w-100">
                                            <InputGroup.Text>{customerCurrencyCode}</InputGroup.Text>
                                            <Form.Control
                                                type="text"
                                                inputMode="decimal"
                                                value={displayPrice}
                                                onChange={(e) =>
                                                    updateLeftField(
                                                        accessory.shopMotorcycleExtraId,
                                                        accessory.tempId,
                                                        "priceText",
                                                        e.target.value
                                                    )
                                                }
                                                onBlur={() => {
                                                    // blur 再把 string -> cents
                                                    const cents =
                                                        typeof toCents === "function"
                                                            ? toCents(displayPrice)
                                                            : displayPrice;

                                                    updateLeftField(
                                                        accessory.shopMotorcycleExtraId,
                                                        accessory.tempId,
                                                        "priceCents",
                                                        cents
                                                    );

                                                    // 可选：blur 后把 priceText 规范化（例如 12 -> "12.00" 或 "12")
                                                    if (typeof fromCents === "function") {
                                                        const normalized = String(fromCents(cents ?? 0));
                                                        updateLeftField(
                                                            accessory.shopMotorcycleExtraId,
                                                            accessory.tempId,
                                                            "priceText",
                                                            normalized
                                                        );
                                                    }
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") e.currentTarget.blur();
                                                }}
                                            />
                                        </InputGroup>
                                    </div>
                                </div>

                                {/* Description */}
                                <div className="acc-selected__desc">
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        size="sm"
                                        placeholder="Description..."
                                        value={accessory.info || ""}
                                        onChange={(e) =>
                                            updateLeftField(
                                                accessory.shopMotorcycleExtraId,
                                                accessory.tempId,
                                                "info",
                                                e.target.value
                                            )
                                        }
                                    />
                                </div>

                                {/* Grid: size + qty */}
                                <div className="acc-selected__grid">
                                    <div>
                                        {Array.isArray(sizeList) && sizeList.length > 0 ? (
                                            <Form.Group>
                                                <Form.Label className="small mb-1">Size</Form.Label>
                                                <Form.Select
                                                    size="sm"
                                                    value={accessory.size || ""}
                                                    onChange={(e) =>
                                                        updateLeftField(
                                                            accessory.shopMotorcycleExtraId,
                                                            accessory.tempId,
                                                            "size",
                                                            e.target.value
                                                        )
                                                    }
                                                >
                                                    {/* 允许空值 */}
                                                    <option value="">Select size...</option>
                                                    {sizeList.map((s) => (
                                                        <option key={s} value={s}>
                                                            {s}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        ) : (
                                            <div />
                                        )}
                                    </div>

                                    <div>
                                        <Form.Group>
                                            <Form.Label className="small mb-1">Quantity</Form.Label>
                                            <Form.Control
                                                size="sm"
                                                type="number"
                                                min="1"
                                                value={accessory.qty ?? 1}
                                                onChange={(e) =>
                                                    updateLeftField(
                                                        accessory.shopMotorcycleExtraId,
                                                        accessory.tempId,
                                                        "qty",
                                                        Math.max(1, parseInt(e.target.value, 10) || 1)
                                                    )
                                                }
                                            />
                                        </Form.Group>
                                    </div>
                                </div>

                                {/* Delete personalized */}
                                {isPersonalized && (
                                    <div className="d-flex justify-content-end mt-2">
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            className="acc-btn"
                                            onClick={() => removePersonalized(accessory.tempId)}
                                        >
                                            <i className="bi bi-trash me-1"></i>
                                            Delete
                                        </Button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default AccessoriesSelectedPanel;